import React, { FC, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "./style.scss";
import Arrow from "../../../../assets/Arrow.svg";
import Cancel from "../../../../assets/Cancel.svg";
import Close from "../../../../assets/Close.svg";
import Info from "../../../../assets/TryMyPriceInfo.svg";
import Open from "../../../../assets/GoBackApp.svg";
import Pending from "../../../../assets/Pending.svg";
import RemoveTransaction from "../../../../assets/RemoveTransaction.svg";
import Rejected from "../../../../assets/Rejected.svg";
import Successful from "../../../../assets/Successful.svg";
import Timeout from "../../../../assets/Timeout.svg";
import AssetIcon from "../asset-icon";
import ExchangeSource from "widgets/market/exchange-source";
import {
  canCancelDeal,
  getCurrSymbol,
  navigateToAsset,
} from "helpers/functions";
import { GetCurrByCode } from "helpers/currencies";
import useExchangeTimeFrame from "hooks/useExchangeTimeFrame";
import useCountdownToClose from "hooks/useCountdownToClose";
import { AssetType, OperationStatus, OperationType } from "static/types";
import { IAsset, marketStore } from "entities/market";
import { accountStore } from "entities/account";
import { useStore } from "zustand";
import { toast } from "react-toastify";
import usePrice from "hooks/usePrice";

interface IActivityType {
  id: number;
  t: "payment" | "deposit" | "withdrawal";
  type: "payment" | "deposit" | "withdrawal" | "commission" | "adjustment";
  status: number;
  amount: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
  // Deposits/withdrawals only — when the money was credited/sent. Payments
  // have no such column, so the row falls back to updatedAt for those.
  settlement_date?: string | null;
  comment: string;
  asset: {
    symbol: string;
  };
}

type ActivitiesProps = {
  activity: IActivityType;
  onTrigger: any;
};

const statusTitles = [
  "Pending",
  "Rejected",
  "Error",
  "Successful",
  "Cancellation",
  "Cancelled",
  "Returned",
];

enum SendStatus {
  Processed = 1,
  Reject = 2,
  Error = 3,
  Send = 4,
  CancellationRequest = 5,
  Cancelled = 6,
  Returned = 7,
}

export const Activity: React.FC<ActivitiesProps> = ({
  activity,
  onTrigger,
}) => {
  const { t: a } = useTranslation("app");

  const PaymentTypes: Record<string, (as_symbol: string) => string> = {
    // const PaymentTypes: any = {
    dividend: (as_symbol: string) => `${a("dividendPayment")} (${as_symbol})`,
    coupon: (as_symbol: string) => `${a("couponPayment")} (${as_symbol})`,
    nominal: (as_symbol: string) => `${a("bondRedemption")} (${as_symbol})`,
    deposit: (as_symbol: string) => `${a("deposit")}`,
    withdrawal: (as_symbol: string) => `${a("withdrawal")}`,
  };

  const {
    id,
    asset,
    t,
    type,
    amount,
    currency,
    status,
    createdAt,
    comment,
    updatedAt,
    settlement_date,
  } = activity;

  // @ts-ignore
  const symbol = t == "payment" ? currency?.symbol : getCurrSymbol(currency);

  const { isLoading, cancelWithdrawal } = useStore(accountStore);

  const cancel = () => {
    cancelWithdrawal(id, onTrigger);
  };

  return (
    <div className="activities-row">
      <div className="price-and-activity">
        {/* <p>
          {["deposit", "payment"].includes(t) ? "+" : "-"}
          {+amount.toFixed(2)}
          {symbol}
        </p> */}
        <p>
          {["deposit", "payment"].includes(t) ? "+" : "-"}
          {amount !== undefined ? (+amount).toFixed(2) : "0.00"}
          {symbol}
        </p>
        <span>
          {["payment", "deposit", "withdrawal"].includes(t)
            ? PaymentTypes[type]
              ? PaymentTypes[type](asset?.symbol || "")
              : comment
            : comment}{" "}
          {a("at")}{" "}
          {moment(t === "payment" ? updatedAt : settlement_date).format(
            "DD.MM.YYYY HH:mm"
          )}
        </span>
      </div>
      <div className="status">
        {!status ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <p className={statusTitles[SendStatus.Send - 1]}>
              {a(statusTitles[SendStatus.Send - 1])}
            </p>
            <img src={Successful} alt={statusTitles[SendStatus.Send - 1]} />
          </div>
        ) : (
          <>
            <div className="status-field">
              <p className={statusTitles[status - 1]}>
                {a(statusTitles[status - 1])}
              </p>
              <img
                src={
                  status === SendStatus.Processed ||
                  status === SendStatus.CancellationRequest
                    ? Pending
                    : status === SendStatus.Send
                    ? Successful
                    : Rejected
                }
                alt={statusTitles[status - 1]}
              />
            </div>
            {status === SendStatus.Processed && (
              <button
                onClick={cancel}
                disabled={isLoading}
                className="cancel-pending-deal"
              >
                <span>{a("cancel")}</span>
                <img src={RemoveTransaction} alt="RemoveTransaction" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Transactions Component -------------------------------------------------

type InfoRowsBlockProps = {
  row: 1 | 2;
  rows: any[];
  commission: number;
  status: number;
  contentOpen: boolean;
  setContentOpen: (value: boolean) => void;
};

const InfoRowsBlock: FC<InfoRowsBlockProps> = ({
  row,
  rows,
  commission,
  status,
  contentOpen,
  setContentOpen,
}) => {
  return (
    <div className="any-transaction-bottomsheet__body__block">
      {row === 1 &&
        rows.map(
          (row, index) =>
            row.title && (
              <div
                key={index}
                className="any-transaction-bottomsheet__body__block__info-row"
              >
                <p
                  {...(row.content &&
                    commission > 0 && {
                      style: {
                        color: "#4D5975",
                        fontWeight: 700,
                      },
                    })}
                >
                  {row.title}
                  {row.info && isPendingStatus(status) && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <img src={Info} alt="Info" />

                      <div>
                        <p>{row.info}</p>
                      </div>
                    </div>
                  )}
                </p>

                <p
                  {...(row.content &&
                    commission > 0 && {
                      style: {
                        color: "#4D5975",
                        fontWeight: 600,
                        cursor: "pointer",
                      },
                      onClick: (e) => {
                        e.stopPropagation();
                        setContentOpen(!contentOpen);
                      },
                    })}
                >
                  {row.value}

                  {row.content && commission > 0 && (
                    <img
                      src={Open}
                      alt="Open"
                      style={{
                        rotate: contentOpen ? "90deg" : "-90deg",
                        width: "18px",
                        cursor: "pointer",
                        transition: "0.25s",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setContentOpen(!contentOpen);
                      }}
                    />
                  )}
                </p>

                {row.content && contentOpen && commission > 0 && (
                  <p
                    style={{
                      width: "100%",
                      textAlign: "right",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    {row.content}
                  </p>
                )}
              </div>
            )
        )}

      {row === 2 &&
        rows.map((row, index) => (
          <div
            key={index}
            className="any-transaction-bottomsheet__body__block__info-row"
          >
            <p>{row.title}</p>
            <p>{row.value}</p>
          </div>
        ))}
    </div>
  );
};

interface IDealType {
  id: number;
  operation: "buy" | "sell";
  status: number;
  commission: number;
  commission_completed: number;
  commission_amd: number;
  quantity: number;
  quantity_complited: number;
  price: number;
  asset: IAsset;
  currency: {
    symbol: string;
    code: string;
  };
  type: string;
  tif: string;
  assignment: null | {
    id: number;
    document: string;
    updatedAt: string;
  };
  order_open_time: string;
  order_close_time: string;
  specific_data?: null | {
    amount?: number;
    accrued_interest?: number;
  };
}

type TransactionsProps = {
  deal: IDealType;
  colored?: boolean;
  type?: "currency" | "asset";
  onSheetToggle?: (isOpen: boolean) => void;
};

const isSuccessStatus = (status: number) => {
  return [
    OperationStatus.Completed,
    OperationStatus.PartiallyCompleted,
  ].includes(status);
};

const isPendingStatus = (status: number) => {
  return [
    OperationStatus.Actively,
    OperationStatus.OnPerformance,
    OperationStatus.CancellationRequest,
  ].includes(status);
};

const isNotCancellingStatus = (status: number) => {
  return [OperationStatus.Actively, OperationStatus.OnPerformance].includes(
    status
  );
};

export const Transaction: React.FC<TransactionsProps> = ({
  deal,
  colored,
  type,
  onSheetToggle,
}) => {
  const { t, i18n } = useTranslation("app");
  const navigate = useNavigate();
  const { isLoading, cancelDeal } = useStore(marketStore);
  const {
    operation,
    status,
    commission,
    commission_amd,
    commission_completed,
    quantity,
    quantity_complited,
    price,
    currency,
    asset,
    assignment,
    specific_data,
  } = deal;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [confirmCancellation, setConfirmCancellation] = useState(false);
  const [confirmCancellationBottomSheet, setConfirmCancellationBottomSheet] =
    useState(false);
  const [contentOpen, setContentOpen] = useState(false);

  // symbols
  //todo after UATE release remove isUate checkers
  const isUate = asset.symbol === "UATE_CN";

  const formattedDate = moment(deal.order_open_time)
    .locale(i18n.language)
    .format("DD MMM YYYY HH:mm");

  const formattedCloseDate = moment(deal.order_close_time)
    .locale(i18n.language)
    .format("DD MMM YYYY HH:mm");

  const exchange = useExchangeTimeFrame(deal?.asset?.exchange);

  const countdown = useCountdownToClose(
    asset.exchange?.after_close || "",
    asset.exchange?.timezone || ""
  );

  const isBond = [AssetType.Bond, AssetType.EuroBond].includes(asset.type);

  const {
    ask: askPrice,
    bid: bidPrice,
    last: marketPrice,
  } = usePrice({
    exchange: exchange?.exchange_code,
    exchangeStatus: exchange?.status,
    assetType: asset.type,
    symbol: asset.symbol,
    ask: asset.ask,
    bid: asset.bid,
    price: asset.price,
    change: asset.change,
  });

  useEffect(() => {
    onSheetToggle?.(isSheetOpen);
  }, [isSheetOpen, onSheetToggle]);

  const getTotalAmount = (): number => {
    if (isSuccessStatus(status)) {
      let total = 0;
      if (status === OperationStatus.Completed && specific_data?.amount) {
        total = specific_data.amount;
      } else {
        total = price * quantity_complited;
      }

      if (operation === OperationType.Buy) {
        total = total + Math.abs(commission_completed);
      } else {
        total = total - Math.abs(commission_completed);
      }

      return +total.toFixed(2);
    } else {
      let total = price * quantity;

      if (operation === OperationType.Buy) {
        total = total + Math.abs(commission);
      } else {
        if (specific_data?.amount) {
          total = specific_data.amount;
        }

        total = total - Math.abs(commission);
      }

      return +total.toFixed(2);
    }
  };

  const getTotalCommission = (): number => {
    if (isSuccessStatus(status)) {
      return +Math.abs(commission_completed).toFixed(2);
    } else {
      return +Math.abs(commission).toFixed(2);
    }
  };

  const getTotalProceed = (): number => {
    if (isSuccessStatus(status)) {
      if (status === OperationStatus.Completed) {
        if (specific_data?.amount) {
          return specific_data.amount;
        }
      }

      let total = price * quantity_complited;

      return +total.toFixed(2);
    } else {
      if (specific_data?.amount) {
        return specific_data.amount;
      }

      let total = price * quantity;

      return +total.toFixed(2);
    }
  };

  const estimatedContent = (): string => {
    if (type === "asset") {
      if (operation === OperationType.Sell) {
        return `= ${
          specific_data?.accrued_interest
            ? getTotalProceed() +
              specific_data?.accrued_interest *
                (isSuccessStatus(status) ? quantity_complited : quantity)
            : getTotalProceed()
        }${
          GetCurrByCode(asset.currency.code)?.symbol
        } - ${getTotalCommission()}${
          GetCurrByCode(asset.currency.code)?.symbol
        } ${t("fee")}`;
      } else {
        return `= ${
          specific_data?.accrued_interest
            ? getTotalProceed() +
              specific_data?.accrued_interest *
                (isSuccessStatus(status) ? quantity_complited : quantity)
            : getTotalProceed()
        }${
          GetCurrByCode(asset.currency.code)?.symbol
        } + ${getTotalCommission()}${
          GetCurrByCode(asset.currency.code)?.symbol
        } ${t("fee")}`;
      }
    } else {
      if (operation === OperationType.Sell) {
        return `= ${getTotalProceed()}${
          currency.symbol
        } - ${getTotalCommission()}${currency.symbol} ${t("fee")}`;
      } else {
        return `= ${getTotalProceed()}${
          GetCurrByCode(asset.currency.code)?.symbol
        } + ${getTotalCommission()}${
          GetCurrByCode(asset.currency.code)?.symbol
        } ${t("fee")}`;
      }
    }
    // ${"\u00A0"}${"\u00A0"}
  };

  const currencyBlock1InfoRows = [
    {
      title: `${t("amount") + ":"}`,
      value:
        operation === OperationType.Sell
          ? `${isSuccessStatus(status) ? quantity_complited : quantity}${
              GetCurrByCode(asset.symbol)?.symbol
            }`
          : `${getTotalProceed()}${currency.symbol}`,
    },

    {
      title: `${t("price") + ":"}`,
      value: `1${GetCurrByCode(asset.symbol)?.symbol} = ${price}${
        GetCurrByCode(asset.currency.code)?.symbol
      }`,
      ...(deal.type === "limit" &&
        isPendingStatus(status) && {
          info:
            operation === OperationType.Sell
              ? t("orderExecutionCondition")
              : t("orderExecutionCondition2"),
        }),
    },

    {
      title: `${t("exchanged") + ":"}`,
      value:
        operation === OperationType.Sell
          ? `${getTotalProceed()}${currency.symbol}`
          : `${isSuccessStatus(status) ? quantity_complited : quantity}${
              GetCurrByCode(asset.symbol)?.symbol
            }`,
    },

    {
      title: t("fees") + ":",
      value: `${getTotalCommission()}${
        GetCurrByCode(asset.currency.code)?.symbol
      } ${
        GetCurrByCode(asset.currency.code)?.symbol !== "֏" &&
        getTotalCommission() > 0
          ? `(${+commission_amd.toFixed(2)}֏)`
          : ""
      }`,
    },

    {
      title: isPendingStatus(status)
        ? operation === OperationType.Sell
          ? t("estimatedProceed") + ":"
          : t("estimatedTotalCost") + ":"
        : operation === OperationType.Sell
        ? t("totalProceed") + ":"
        : t("totalCost") + ":",

      value: `${getTotalAmount()}${GetCurrByCode(asset.currency.code)?.symbol}`,

      info:
        deal.type === "limit"
          ? t("finalProceedCanBeBetterThanEstimated")
          : t("marketPriceCanChangeWhileProcessingYourOrder"),

      content: estimatedContent(),
    },
  ];

  const currencyBlock2InfoRows = [
    {
      title: t("type") + ":",
      value:
        deal.type === "limit"
          ? t("limitOrder")
          : // : deal.type === "Market"
            t("marketOrder"),
      // : t("tradeWithSirius"),
      // value:
      //   deal.type === "limit"
      //     ? t("limitOrder")
      //     : t("marketOrder")
    },
    {
      title: t("placed") + ":",
      value: formattedDate,
    },
    {
      title: isPendingStatus(status)
        ? t("activeTill") + ":"
        : isSuccessStatus(status)
        ? t("succeedAt") + ":"
        : t("rejectedAt") + ":",

      value: isPendingStatus(status) ? t("tillCancelled") : formattedCloseDate,
    },
  ];

  const assetBlock1InfoRows = [
    {
      title: `${
        operation === OperationType.Sell
          ? isPendingStatus(status)
            ? t("youWillSell") + ":"
            : t("youSold") + ":"
          : isPendingStatus(status)
          ? t("youWillGet") + ":"
          : t("youReceived") + ":"
      }`,
      value: `${isSuccessStatus(status) ? quantity_complited : quantity} ${
        quantity_complited == 1 || quantity == 1 ? t("unit") : t("units2")
      }`,
    },

    {
      title: isPendingStatus(status)
        ? deal.type === "limit"
          ? t("limitPricePerUnit") + ":"
          : t("pricePerUnit") + ":"
        : t("pricePerUnit") + ":",
      value: `${price}${GetCurrByCode(asset.currency.code)?.symbol}`,
      ...(deal.type === "limit" &&
        isPendingStatus(status) && {
          info:
            operation === OperationType.Sell
              ? t("orderExecutionCondition")
              : t("orderExecutionCondition2"),
        }),
    },

    isBond && specific_data?.accrued_interest
      ? {
          title: t("ACI_full2") + ":",
          value: `${+specific_data.accrued_interest.toFixed(4)}${
            GetCurrByCode(asset.currency.code)?.symbol
          }`,
        }
      : {},

    {
      title: t("fees") + ":",
      value: commission
        ? `${getTotalCommission()}${GetCurrByCode(asset.currency.code)?.symbol}`
        : 0,
    },

    isBond
      ? {
          title: t("Yield to maturity") + ":",
          value: `${
            operation === OperationType.Sell
              ? +asset.data.bid_yield.toFixed(2)
              : +asset.data.ask_yield.toFixed(2)
          }%`,
        }
      : {},

    {
      title: isPendingStatus(status)
        ? operation === OperationType.Sell
          ? t("estimatedProceed") + ":"
          : t("estimatedTotalCost") + ":"
        : operation === OperationType.Sell
        ? t("totalProceed") + ":"
        : t("totalCost") + ":",
      value: `${
        specific_data?.accrued_interest
          ? getTotalAmount() +
            specific_data?.accrued_interest *
              (isSuccessStatus(status) ? quantity_complited : quantity)
          : getTotalAmount()
      }${GetCurrByCode(asset.currency.code)?.symbol}`,
      info:
        deal.type === "limit"
          ? t("finalProceedCanBeBetterThanEstimated")
          : t("marketPriceCanChangeWhileProcessingYourOrder"),
      // content: `=${"\u00A0"}${"\u00A0"}${getTotalAmount()}${
      //   GetCurrByCode(asset.currency.code)?.symbol
      // }${"\u00A0"}${"\u00A0"}+${"\u00A0"}${"\u00A0"}${getTotalCommission()}${
      //   GetCurrByCode(asset.currency.code)?.symbol
      // } ${t("fee")}`,
      content: estimatedContent(),
    },
  ];

  const assetBlock2InfoRows = [
    {
      title: t("type") + ":",
      value:
        deal.type === "limit"
          ? t("limitOrder")
          : // : deal.type === "Market"
            t("marketOrder"),
      // : t("tradeWithSirius"),
    },

    {
      title: t("placed") + ":",
      value: formattedDate,
    },

    {
      title: isPendingStatus(status)
        ? deal.tif === "day" && countdown
          ? t("expiresIn") + ":"
          : t("activeTill") + ":"
        : isSuccessStatus(status)
        ? t("succeedAt") + ":"
        : t("rejectedAt") + ":",

      value: isPendingStatus(status)
        ? deal.tif === "day" && countdown
          ? countdown
          : t("tillCancelled") + "(GTC)"
        : formattedCloseDate,
    },
  ];

  const rows = type === "asset" ? assetBlock1InfoRows : currencyBlock1InfoRows;
  const rows2 = type === "asset" ? assetBlock2InfoRows : currencyBlock2InfoRows;

  // useEffect(() => {
  //   console.log(deal);
  // }, [deal]);
  // symbol: 'UATE_CN'

  if (isPendingStatus(status)) {
    if (type === "asset") {
      return (
        <div
          className={`active-transaction-row ${
            colored ? "active-transaction-row-colored" : ""
          }`}
          style={{
            cursor: isNotCancellingStatus(status) ? "pointer" : "default",
          }}
          onClick={() => {
            if (isUate) {
            } else {
              isNotCancellingStatus(status) && setIsSheetOpen(true);
              setConfirmCancellation(false);
            }
          }}
        >
          {deal.tif && deal.tif === "day" && countdown && (
            <div className="active-transaction-row-header-timeout">
              <div>
                <img src={Timeout} alt="Timeout" />
                <p>{t("expiresIn")}:</p>
              </div>
              <p>{countdown}</p>
            </div>
          )}

          <div className="active-transaction-row-header">
            <div
              className="active-asset-transaction-row-header-left"
              onClick={() => navigateToAsset(asset.type, asset.id, navigate)}
            >
              <AssetIcon asset={asset} />
              <div className="name-type">
                <p>
                  {asset.symbol} | {asset.name}
                </p>
                <img src={Open} alt="OpenAsset" />
              </div>
            </div>

            <div className="active-transaction-row-header-right">
              <span
                style={{
                  fontSize: "12px",
                  marginBottom: "-4px",
                  whiteSpace: "nowrap",
                }}
              >
                {formattedDate}
              </span>
              {/* //todo remove isUate checker */}
              {isUate ? (
                <p>
                  {operation === OperationType.Sell ? "" : "-"}{" "}
                  {specific_data?.accrued_interest
                    ? +(
                        getTotalAmount() +
                        specific_data?.accrued_interest *
                          (isSuccessStatus(status)
                            ? quantity_complited
                            : quantity)
                      ).toFixed(2)
                    : +getTotalAmount().toFixed(2)}
                  {currency.symbol}
                </p>
              ) : (
                <p>
                  {operation === OperationType.Sell ? "-" : "+"}
                  {quantity} {quantity == 1 ? t("unit") : t("units2")}
                </p>
              )}
              {!isUate && (
                <span>
                  {operation === OperationType.Sell ? "" : "-"}
                  {specific_data?.accrued_interest
                    ? +(
                        getTotalAmount() +
                        specific_data?.accrued_interest *
                          (isSuccessStatus(status)
                            ? quantity_complited
                            : quantity)
                      ).toFixed(2)
                    : +getTotalAmount().toFixed(2)}

                  {currency.symbol}
                </span>
              )}
            </div>
          </div>

          <hr />

          <div className="active-transaction-row-body">
            <div className="active-transaction-row-body-left">
              {isUate ? (
                <></>
              ) : (
                <p>
                  {isBond
                    ? t("Net price") + ":"
                    : deal.type === "limit"
                    ? t("Market price") + ":"
                    : t("price") + ":"}{" "}
                  {(operation === OperationType.Buy ? askPrice : bidPrice) ||
                    marketPrice}
                  {isBond ? "%" : GetCurrByCode(asset.currency.code)?.symbol}
                </p>
              )}

              {deal.type === "limit" && (
                <p>
                  {operation === OperationType.Sell
                    ? t("sellWhen")
                    : t("buyWhen")}
                  : {price}
                  {GetCurrByCode(asset.currency.code)?.symbol}
                </p>
              )}
            </div>

            <div className="active-transaction-row-body-right">
              {isNotCancellingStatus(status) ? (
                confirmCancellation ? (
                  <button
                    className="active-transaction-row-body-right__button-confirm"
                    onClick={(e) => {
                      e.stopPropagation();
                      const { canCancel, errorMessage } = canCancelDeal(
                        deal,
                        t
                      );
                      if (canCancel) {
                        cancelDeal(deal.id);
                      } else {
                        toast.error(errorMessage);
                      }
                    }}
                    disabled={isLoading}
                  >
                    <span>{t("confirm")}</span>
                    <img
                      style={{
                        rotate: "180deg",
                        width: "16px",
                      }}
                      src={Open}
                      alt="Open"
                    />
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmCancellation(true);
                    }}
                    disabled={isLoading}
                    style={{ opacity: isLoading ? 0.5 : 1 }}
                  >
                    <img src={Cancel} alt="Cancel" />
                    <span>{t("cancel")}</span>
                  </button>
                )
              ) : (
                <button disabled style={{ opacity: 0.5 }}>
                  <img src={Cancel} alt="Cancel" />
                  <span>{t("Cancellation")}...</span>
                </button>
              )}
            </div>
          </div>

          {isSheetOpen && (
            <div
              className="any-transaction-bottomsheet-overlay"
              onClick={(e) => {
                e.stopPropagation();
                setIsSheetOpen(false);
              }}
            >
              <div
                className="any-transaction-bottomsheet"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="any-transaction-bottomsheet__header">
                  <p className="any-transaction-bottomsheet__header__title">
                    {operation === OperationType.Sell ? t("sell") : t("buy")}
                  </p>

                  <button
                    onClick={() => setIsSheetOpen(false)}
                    aria-label={t("close")}
                  >
                    <img src={Close} alt={t("close")} />
                  </button>
                </div>

                <div className="any-transaction-bottomsheet__body">
                  <div className="any-transaction-bottomsheet__body__header">
                    <div
                      className="any-transaction-bottomsheet__body__header__left"
                      onClick={() =>
                        navigateToAsset(asset.type, asset.id, navigate)
                      }
                    >
                      <AssetIcon asset={asset} />

                      <p>
                        {asset.symbol} | {asset.name}
                      </p>

                      <div>
                        <img src={Open} alt="OpenAsset" />
                      </div>
                    </div>

                    {exchange && (
                      <ExchangeSource
                        name={exchange.exchange_name}
                        code={exchange.exchange_code}
                        image={exchange.exchange_logo}
                      />
                    )}
                  </div>

                  <InfoRowsBlock
                    row={1}
                    rows={rows}
                    commission={getTotalCommission()}
                    status={status}
                    contentOpen={contentOpen}
                    setContentOpen={setContentOpen}
                  />
                  <InfoRowsBlock
                    row={2}
                    rows={rows2}
                    commission={getTotalCommission()}
                    status={status}
                    contentOpen={contentOpen}
                    setContentOpen={setContentOpen}
                  />

                  <button
                    className={
                      confirmCancellationBottomSheet
                        ? "any-transaction-bottomsheet__body__button any-transaction-bottomsheet__body__button-confirm-active"
                        : "any-transaction-bottomsheet__body__button"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirmCancellationBottomSheet) {
                        const { canCancel, errorMessage } = canCancelDeal(
                          deal,
                          t
                        );
                        if (canCancel) {
                          cancelDeal(deal.id);
                        } else {
                          toast.error(errorMessage);
                        }
                        setIsSheetOpen(false);
                      } else {
                        setConfirmCancellationBottomSheet(true);
                      }
                    }}
                  >
                    {confirmCancellationBottomSheet
                      ? t("confirm")
                      : t("requestToCancel")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div
          className={`active-transaction-row ${
            colored ? "active-transaction-row-colored" : ""
          }`}
          style={{
            cursor: isNotCancellingStatus(status) ? "pointer" : "default",
          }}
          onClick={() => {
            isNotCancellingStatus(status) && setIsSheetOpen(true);
            setConfirmCancellation(false);
          }}
        >
          <div className="active-transaction-row-header">
            <div className="active-transaction-row-header-left">
              <AssetIcon
                asset={asset}
                swapCurrency={operation === OperationType.Buy}
              />
              <div className="name-type">
                <p>
                  {operation === OperationType.Sell
                    ? asset.symbol
                    : asset.currency.code}
                </p>
                <img src={Arrow} alt="Arrow" />
                <p>
                  {operation === OperationType.Sell
                    ? asset.currency.code
                    : asset.symbol}
                </p>
                {/* <p>{operation}</p> */}
              </div>
            </div>

            {/* {operation === OperationType.Sell ? www : www} */}

            <div className="active-transaction-row-header-right">
              <span style={{ fontSize: "12px", marginBottom: "-4px" }}>
                {formattedDate}
              </span>
              <p style={{ color: "#38AF6C" }}>
                +
                {operation === OperationType.Sell
                  ? `${getTotalProceed()} ${currency.symbol}`
                  : `${quantity} ${GetCurrByCode(asset.symbol)?.symbol}`}
              </p>
              <span>
                -
                {operation === OperationType.Sell
                  ? `${quantity} ${GetCurrByCode(asset.symbol)?.symbol}`
                  : `${getTotalProceed()} ${currency.symbol}`}
              </span>
            </div>
          </div>

          <hr />

          <div className="active-transaction-row-body">
            <div className="active-transaction-row-body-left">
              <p>
                {deal.type === "limit" ? t("Market price") : t("price")}: 1
                {GetCurrByCode(asset.symbol)?.symbol} ={" "}
                {operation === OperationType.Buy ? asset.ask : asset.bid}
                {GetCurrByCode(asset.currency.code)?.symbol}
              </p>
              {deal.type === "limit" && (
                <p>
                  {t("exchangeWhen")}: 1{GetCurrByCode(asset.symbol)?.symbol} ={" "}
                  {price}
                  {GetCurrByCode(asset.currency.code)?.symbol}
                </p>
              )}
            </div>

            <div className="active-transaction-row-body-right">
              {isNotCancellingStatus(status) ? (
                confirmCancellation ? (
                  <button
                    className="active-transaction-row-body-right__button-confirm"
                    onClick={(e) => {
                      e.stopPropagation();
                      const { canCancel, errorMessage } = canCancelDeal(
                        deal,
                        t
                      );
                      if (canCancel) {
                        cancelDeal(deal.id);
                      } else {
                        toast.error(errorMessage);
                      }
                    }}
                    disabled={isLoading}
                  >
                    <span>{t("confirm")}</span>
                    <img
                      style={{
                        rotate: "180deg",
                        width: "16px",
                      }}
                      src={Open}
                      alt="Open"
                    />
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmCancellation(true);
                    }}
                    disabled={isLoading}
                    style={{ opacity: isLoading ? 0.5 : 1 }}
                  >
                    <img src={Cancel} alt="Cancel" />
                    <span>{t("cancel")}</span>
                  </button>
                )
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const { canCancel, errorMessage } = canCancelDeal(deal, t);
                    if (canCancel) {
                      cancelDeal(deal.id);
                    } else {
                      toast.error(errorMessage);
                    }
                  }}
                  disabled
                  style={{ opacity: 0.5 }}
                >
                  <img src={Cancel} alt="Cancel" />
                  <span>{t("Cancellation")}...</span>
                </button>
              )}
            </div>
          </div>

          {isSheetOpen && (
            <div
              className="any-transaction-bottomsheet-overlay"
              onClick={(e) => {
                e.stopPropagation();
                setIsSheetOpen(false);
                setConfirmCancellationBottomSheet(false);
              }}
            >
              <div
                className="any-transaction-bottomsheet"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmCancellationBottomSheet(false);
                }}
              >
                <div className="any-transaction-bottomsheet__header">
                  <div className="any-transaction-bottomsheet__header__left">
                    <AssetIcon
                      asset={asset}
                      swapCurrency={operation === OperationType.Buy}
                    />
                    <div className="name-type">
                      <p>
                        {operation === OperationType.Sell
                          ? asset.symbol
                          : asset.currency.code}
                      </p>
                      <img src={Arrow} alt="Arrow" />
                      <p>
                        {operation === OperationType.Sell
                          ? asset.currency.code
                          : asset.symbol}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setIsSheetOpen(false)}
                    aria-label={t("close")}
                  >
                    <img src={Close} alt={t("close")} />
                  </button>
                </div>

                <div className="any-transaction-bottomsheet__body">
                  <InfoRowsBlock
                    row={1}
                    rows={rows}
                    commission={getTotalCommission()}
                    status={status}
                    contentOpen={contentOpen}
                    setContentOpen={setContentOpen}
                  />
                  <InfoRowsBlock
                    row={2}
                    rows={rows2}
                    commission={getTotalCommission()}
                    status={status}
                    contentOpen={contentOpen}
                    setContentOpen={setContentOpen}
                  />

                  <button
                    className={
                      confirmCancellationBottomSheet
                        ? "any-transaction-bottomsheet__body__button any-transaction-bottomsheet__body__button-confirm-active"
                        : "any-transaction-bottomsheet__body__button"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirmCancellationBottomSheet) {
                        const { canCancel, errorMessage } = canCancelDeal(
                          deal,
                          t
                        );
                        if (canCancel) {
                          cancelDeal(deal.id);
                        } else {
                          toast.error(errorMessage);
                        }
                        setIsSheetOpen(false);
                      } else {
                        setConfirmCancellationBottomSheet(true);
                      }
                    }}
                  >
                    {confirmCancellationBottomSheet
                      ? t("confirm")
                      : t("requestToCancel")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
  } else {
    if (type === "asset") {
      return (
        <div
          className={`currency-transaction-row ${
            colored ? "currency-transaction-row-colored" : ""
          }`}
          onClick={() => {
            if (isUate) {
            } else {
              setIsSheetOpen(true);
            }
          }}
        >
          <div className="currency-transaction-row-header">
            <div className="currency-transaction-row-header-left asset-transaction-row-header-left">
              <AssetIcon asset={asset} />
              <div className="name-type">
                <p>
                  {asset.symbol} <br /> {asset.name}
                </p>
              </div>
            </div>

            <div className="currency-transaction-row-header-right asset-transaction-row-header-right">
              <span
                style={{
                  fontSize: "12px",
                  marginBottom: "-4px",
                  whiteSpace: "nowrap",
                }}
              >
                {formattedDate}
              </span>
              {isUate ? (
                <p>
                  {operation === OperationType.Sell ? "+" : "-"}
                  {specific_data?.accrued_interest
                    ? getTotalAmount() +
                      specific_data?.accrued_interest *
                        (isSuccessStatus(status)
                          ? quantity_complited
                          : quantity)
                    : getTotalAmount()}
                  {currency.symbol}
                </p>
              ) : (
                <p>
                  {operation === OperationType.Sell ? "-" : "+"}
                  {isSuccessStatus(status) ? quantity_complited : quantity}
                  {quantity == 1 ? " " + t("unit") : " " + t("units2")}
                  <img
                    src={
                      isPendingStatus(status)
                        ? Pending
                        : isSuccessStatus(status)
                        ? Successful
                        : Rejected
                    }
                    alt={String(status)}
                  />
                </p>
              )}

              {!isUate && (
                <span>
                  {operation === OperationType.Sell ? "+" : "-"}
                  {specific_data?.accrued_interest
                    ? getTotalAmount() +
                      specific_data?.accrued_interest *
                        (isSuccessStatus(status)
                          ? quantity_complited
                          : quantity)
                    : getTotalAmount()}
                  {currency.symbol}
                </span>
              )}
            </div>
          </div>

          {isSheetOpen && (
            <div
              className="any-transaction-bottomsheet-overlay"
              onClick={(e) => {
                e.stopPropagation();
                setIsSheetOpen(false);
                setConfirmCancellationBottomSheet(false);
              }}
            >
              <div
                className="any-transaction-bottomsheet"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmCancellationBottomSheet(false);
                }}
              >
                <div className="any-transaction-bottomsheet__header">
                  <p className="any-transaction-bottomsheet__header__title">
                    {operation === OperationType.Sell
                      ? t("youSold")
                      : t("youReceived")}
                  </p>

                  <button
                    onClick={() => setIsSheetOpen(false)}
                    aria-label={t("close")}
                  >
                    <img src={Close} alt={t("close")} />
                  </button>
                </div>

                <div className="any-transaction-bottomsheet__body">
                  {isPendingStatus(status) || isSuccessStatus(status) ? (
                    ""
                  ) : (
                    <div className="any-transaction-bottomsheet__body__rejected">
                      {t("rejected")}
                    </div>
                  )}

                  <div className="any-transaction-bottomsheet__body__header">
                    <div
                      className="any-transaction-bottomsheet__body__header__left"
                      onClick={() =>
                        navigateToAsset(asset.type, asset.id, navigate)
                      }
                    >
                      <AssetIcon asset={asset} />

                      <p>
                        {asset.symbol} | {asset.name}
                      </p>

                      <div>
                        <img src={Open} alt="OpenAsset" />
                      </div>
                    </div>

                    {exchange && (
                      <ExchangeSource
                        name={exchange.exchange_name}
                        code={exchange.exchange_code}
                        image={exchange.exchange_logo}
                      />
                    )}
                  </div>

                  <InfoRowsBlock
                    row={1}
                    rows={rows}
                    commission={getTotalCommission()}
                    status={status}
                    contentOpen={contentOpen}
                    setContentOpen={setContentOpen}
                  />
                  <InfoRowsBlock
                    row={2}
                    rows={rows2}
                    commission={getTotalCommission()}
                    status={status}
                    contentOpen={contentOpen}
                    setContentOpen={setContentOpen}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div
          className={`currency-transaction-row ${
            colored ? "currency-transaction-row-colored" : ""
          }`}
          onClick={() => setIsSheetOpen(true)}
        >
          <div className="currency-transaction-row-header">
            <div className="currency-transaction-row-header-left">
              <AssetIcon
                asset={asset}
                swapCurrency={operation === OperationType.Buy}
              />
              <div className="name-type">
                <p>
                  {operation === OperationType.Sell
                    ? asset.symbol
                    : asset.currency.code}
                </p>
                <img src={Arrow} alt="Arrow" />
                <p>
                  {operation === OperationType.Sell
                    ? asset.currency.code
                    : asset.symbol}
                </p>
              </div>
            </div>

            <div className="currency-transaction-row-header-right">
              <span
                style={{
                  fontSize: "12px",
                  marginBottom: "-2px",
                  whiteSpace: "nowrap",
                }}
              >
                {formattedDate}
              </span>
              <p>
                +
                {operation === OperationType.Sell
                  ? `${getTotalProceed()} ${currency.symbol}`
                  : `${
                      isSuccessStatus(status) ? quantity_complited : quantity
                    } ${GetCurrByCode(asset.symbol)?.symbol}`}
                <img
                  src={
                    isPendingStatus(status)
                      ? Pending
                      : isSuccessStatus(status)
                      ? Successful
                      : Rejected
                  }
                  alt={String(status)}
                />
              </p>
              <span>
                -
                {operation === OperationType.Sell
                  ? `${
                      isSuccessStatus(status) ? quantity_complited : quantity
                    } ${GetCurrByCode(asset.symbol)?.symbol}`
                  : `${getTotalProceed()} ${currency.symbol}`}
              </span>
            </div>
          </div>

          {isSheetOpen && (
            <div
              className="any-transaction-bottomsheet-overlay"
              onClick={(e) => {
                e.stopPropagation();
                setIsSheetOpen(false);
              }}
            >
              <div
                className="any-transaction-bottomsheet"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="any-transaction-bottomsheet__header">
                  <div className="any-transaction-bottomsheet__header__left">
                    <AssetIcon
                      asset={asset}
                      swapCurrency={operation === OperationType.Buy}
                    />
                    <div className="name-type">
                      <p>
                        {operation === OperationType.Sell
                          ? asset.symbol
                          : asset.currency.code}
                      </p>
                      <img src={Arrow} alt="Arrow" />
                      <p>
                        {operation === OperationType.Sell
                          ? asset.currency.code
                          : asset.symbol}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setIsSheetOpen(false)}
                    aria-label={t("close")}
                  >
                    <img src={Close} alt={t("close")} />
                  </button>
                </div>

                <div className="any-transaction-bottomsheet__body">
                  {isPendingStatus(status) || isSuccessStatus(status) ? (
                    ""
                  ) : (
                    <div className="any-transaction-bottomsheet__body__rejected">
                      {t("rejected")}
                    </div>
                  )}

                  <InfoRowsBlock
                    row={1}
                    rows={rows}
                    commission={getTotalCommission()}
                    status={status}
                    contentOpen={contentOpen}
                    setContentOpen={setContentOpen}
                  />
                  <InfoRowsBlock
                    row={2}
                    rows={rows2}
                    commission={getTotalCommission()}
                    status={status}
                    contentOpen={contentOpen}
                    setContentOpen={setContentOpen}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
  }
};
